// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * The complete set of non-success states an administration screen can show.
 *
 * `docs/05-CODING-STANDARDS.md` §26 and `docs/10-TESTING.md` §30 require
 * loading, empty, error, permission-denied, timeout, offline, and degraded
 * states to exist and be safe. Implementing them once here means a feature
 * screen cannot accidentally omit one, and each state is announced to assistive
 * technology rather than only styled.
 *
 * Two safety rules are enforced by construction:
 *
 * - no state renders a raw exception, stack trace, provider error, or internal
 *   hostname — only the safe title carried by the RFC 9457 problem document
 *   (`API-012`, `07-SECURITY.md` §15.5); and
 * - a denied state explains that access is refused by the service; it never
 *   implies the browser made that decision (`CST-048`).
 */

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

import type { ApiProblemError, ProblemKind } from '../api-clients/problem';

/** Props for {@link LoadingState}. */
export interface LoadingStateProps {
  /** Accessible description of what is loading. */
  readonly label: string;
}

/** Busy indicator with an accessible live region. */
export function LoadingState({ label }: LoadingStateProps): ReactNode {
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      role="status"
      aria-live="polite"
      sx={{ py: 3 }}
    >
      <CircularProgress size={24} aria-hidden="true" />
      <Typography variant="body2">{label}</Typography>
    </Stack>
  );
}

/** Props for {@link EmptyState}. */
export interface EmptyStateProps {
  /** Short heading describing the empty collection. */
  readonly title: string;
  /** Optional guidance on what to do next. */
  readonly description?: string;
}

/** Explicit empty state; an empty table is never left unexplained. */
export function EmptyState({ title, description }: EmptyStateProps): ReactNode {
  return (
    <Box sx={{ py: 3 }} role="status">
      <Typography variant="h3" component="p" gutterBottom>
        {title}
      </Typography>
      {description === undefined ? null : (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
  );
}

/** Copy shown for each failure class. */
const PROBLEM_COPY: Record<ProblemKind, { title: string; description: string }> = {
  unauthenticated: {
    title: 'Your session has ended',
    description: 'Sign in again to continue. No information was changed.',
  },
  denied: {
    title: 'Access refused',
    description:
      'The service refused this request for your account. Ask your administrator if you need this access.',
  },
  notFound: {
    title: 'Not found',
    description: 'This item no longer exists or was never visible to your account.',
  },
  conflict: {
    title: 'The item changed',
    description: 'Someone else updated this item. Reload to see the current version.',
  },
  validation: {
    title: 'The request could not be accepted',
    description: 'Correct the highlighted values and try again.',
  },
  rateLimited: {
    title: 'Too many requests',
    description: 'The service is limiting requests. Wait a moment and try again.',
  },
  timeout: {
    title: 'The request timed out',
    description: 'The service did not respond in time. Nothing was changed.',
  },
  offline: {
    title: 'No connection',
    description: 'Your browser could not reach the service. Check your connection and retry.',
  },
  upstreamUnavailable: {
    title: 'Service temporarily unavailable',
    description: 'A dependency is unavailable, so this information cannot be shown right now.',
  },
  server: {
    title: 'Something went wrong',
    description: 'The service could not complete this request. Nothing was changed.',
  },
  client: {
    title: 'The request could not be completed',
    description: 'The service rejected this request.',
  },
};

/** Props for {@link ErrorState}. */
export interface ErrorStateProps {
  /** The failure to present. */
  readonly error: ApiProblemError;
  /** Invoked when the operator chooses to retry, when retrying is safe. */
  readonly onRetry?: () => void;
}

/**
 * Present a failure using only server-supplied safe text.
 *
 * A retry control is offered only for classes where repeating the identical
 * request is safe; `RUN-010` forbids retrying an operation whose outcome is
 * unknown.
 */
export function ErrorState({ error, onRetry }: ErrorStateProps): ReactNode {
  const copy = PROBLEM_COPY[error.kind];
  const severity =
    error.kind === 'denied' || error.kind === 'unauthenticated' ? 'warning' : 'error';
  return (
    <Alert severity={severity} role="alert" sx={{ my: 2 }}>
      <AlertTitle>{copy.title}</AlertTitle>
      <Typography variant="body2">{copy.description}</Typography>
      {error.fieldProblems.length === 0 ? null : (
        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
          {error.fieldProblems.map((problem) => (
            <li key={`${problem.pointer}:${problem.message}`}>
              <Typography variant="body2">
                {problem.pointer}: {problem.message}
              </Typography>
            </li>
          ))}
        </Box>
      )}
      {error.correlationId === undefined ? null : (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Reference for support: {error.correlationId}
        </Typography>
      )}
      {onRetry !== undefined && error.retryable ? (
        <Button size="small" onClick={onRetry} sx={{ mt: 1 }}>
          Try again
        </Button>
      ) : null}
    </Alert>
  );
}

/** Props for {@link PermissionDeniedState}. */
export interface PermissionDeniedStateProps {
  /** Human-readable name of the area that is not available. */
  readonly area: string;
}

/** Permission-denied placeholder for an area the session was not offered. */
export function PermissionDeniedState({ area }: PermissionDeniedStateProps): ReactNode {
  return (
    <Alert severity="warning" role="alert" sx={{ my: 2 }}>
      <AlertTitle>{area} is not available for your account</AlertTitle>
      <Typography variant="body2">
        Your administrator controls this access. Requests are authorised by the service, so this
        screen shows nothing that your account cannot already see.
      </Typography>
    </Alert>
  );
}

/** Props for {@link DegradedBanner}. */
export interface DegradedBannerProps {
  /** Safe operator-facing reasons the experience is degraded. */
  readonly reasons: readonly string[];
}

/**
 * Announce degraded operation.
 *
 * `ADM-014` forbids presenting missing telemetry as healthy, so a degraded
 * dependency is stated plainly rather than hidden behind a zero.
 */
export function DegradedBanner({ reasons }: DegradedBannerProps): ReactNode {
  if (reasons.length === 0) {
    return null;
  }
  return (
    <Alert severity="info" role="status" sx={{ my: 2 }}>
      <AlertTitle>Some information is unavailable</AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 3 }}>
        {reasons.map((reason) => (
          <li key={reason}>
            <Typography variant="body2">{reason}</Typography>
          </li>
        ))}
      </Box>
    </Alert>
  );
}
